import { useMemo } from 'react';
import Combobox from '@/components/Combobox';
import AppliedFilterChip from '@/components/Filter/AppliedFilterChip';
import AvailableFilterChip from '@/components/Filter/AvailableFilterChip';
import FilterTrans from '@/components/Filter/FilterTrans';
import { FilterChipPopoverContent } from '@/components/FilterChip/FilterChipPopoverContent';
import useMultiSelectAvailableFilterChipModel from '@/components/FilterChip/useMultiSelectAvailableFilterChipModel';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import useValueToLabelCache from '@/hooks/useValueToLabelCache';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { useContractTypeLabels } from './-contractTypeLabels';
type Props = {
  value: string[];
  onChange: (value: string[]) => void;
};

/** 未適用状態：チップを押すとポップオーバーで複数選択。閉じた時点で確定（適用/キャンセルボタン無し）。 */
function AvailableChip({
  onChange
}: {
  onChange: (value: string[]) => void;
}) {
  const {
    t: tHead
  } = useTranslation(defaultNS, {
    keyPrefix: 'component.dataTable.headers'
  });
  const {
    t: tFilter
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.contract.list'
  });
  const {
    options
  } = useContractTypeLabels();
  const {
    open,
    selectedIds,
    freeText,
    onFreeTextChange,
    getOnSelectedIdsChange,
    getOnOpenChange
  } = useMultiSelectAvailableFilterChipModel<{
    id: string;
    label: string;
  }>();
  const filteredOptions = freeText ? options.filter(o => o.label.toLowerCase().includes(freeText.toLowerCase())) : options;
  const valueToLabelCache = useValueToLabelCache();
  const onOpenChange = useMemo(() => getOnOpenChange({
    onFilterChange: updaterOrValue => {
      const next = typeof updaterOrValue === 'function' ? updaterOrValue([]) : updaterOrValue;
      onChange(next ?? []);
    }
  }), [getOnOpenChange, onChange]);
  const onSelectedIdsChange = useMemo(() => getOnSelectedIdsChange({
    options
  }), [getOnSelectedIdsChange, options]);
  return <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <AvailableFilterChip showChevron>
          {tHead('contractType')}
        </AvailableFilterChip>
      </PopoverTrigger>
      <FilterChipPopoverContent>
        <Combobox type="multiple" options={filteredOptions} value={selectedIds} onValueChange={onSelectedIdsChange} searchPlaceholder={tFilter('filterPlaceholder')} onSearchChange={onFreeTextChange} valueToLabelCache={valueToLabelCache} />
      </FilterChipPopoverContent>
    </Popover>;
}

/** 適用済み状態：選択中の種別を表示し、X でクリア。 */
function AppliedChip({
  value,
  onChange
}: Props) {
  const {
    t: tHead
  } = useTranslation(defaultNS, {
    keyPrefix: 'component.dataTable.headers'
  });
  const {
    labelFor
  } = useContractTypeLabels();
  return <AppliedFilterChip onClear={() => onChange([])}>
      <FilterTrans sub={tHead('contractType')} type="contains" obj={value.map(labelFor).join(', ')} />
    </AppliedFilterChip>;
}

/**
 * 契約種別の複数選択フィルタ。
 * People 一覧（NetworkTypeFilterChip 等）と同じ FilterChip + Combobox スタックに準拠。
 * 適用/キャンセルボタンは無く、ポップオーバーを閉じた時点で確定する。
 */
export function ContractTypeFilterChip({
  value,
  onChange
}: Props) {
  return value.length === 0 ? <AvailableChip onChange={onChange} /> : <AppliedChip value={value} onChange={onChange} />;
}